import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, Mail, Minus } from 'lucide-react';
import { get, post } from '../../lib/apiClient';
import styles from './Admin.module.css';

/**
 * Admin → Analytics.
 *
 * Top: a row of metric cards (totals, MRR split into plans, add-ons and
 * widget tiers, MAU, signups this month vs last month, and the usage-based
 * pricing cards of TOADO-655). Middle: plan breakdown stacked bar. Bottom:
 * daily signups bar chart with a 30/90/180 day toggle.
 *
 * No charting library — these are HTML/CSS bars + inline SVG.
 * Adding recharts (or any chart lib) would bloat the web bundle for
 * what's a small founder-facing dashboard. If we ever ship admin
 * surfaces to more than a handful of staff, revisit.
 */
interface SummaryResponse {
  totals: { users: number; companies: number; projects: number; tickets: number };
  planBreakdown: Record<string, number>;
  /** Plans, add-ons and widget tiers together; `mrrBreakdown` splits it. */
  mrrCents: number;
  mrrBreakdown: { plansCents: number; addonsCents: number; widgetTiersCents: number };
  pricing: {
    workspacesWithAddons: number;
    widgetTiers: { starter: number; growth: number; scale: number };
    attention: {
      addonChangesWaiting: number;
      obligationsOpen: number;
      addonSubscriptionsPastDue: number;
      tiersNotInUse: number;
      tiersPlanCannotUse: number;
    };
    /** Workspaces that reached 100% of a meter this month, by meter. */
    limitsReachedThisMonth: Record<string, number>;
  };
  mauLast30d: number;
  wauLast7d: number;
  teamAccountCount: number;
  signupsThisMonth: number;
  signupsLastMonth: number;
  widget: { active: number; configured: number; submissions: number };
}

interface SignupsResponse {
  days: number;
  from: string;
  to: string;
  series: Array<{ date: string; signups: number }>;
}

type RangeMode = { kind: 'preset'; days: 30 | 90 | 180 } | { kind: 'custom'; from: string; to: string };

export function AnalyticsPage() {
  const [range, setRange] = useState<RangeMode>({ kind: 'preset', days: 30 });
  const { data: summary, isLoading: loadingSummary } = useQuery({
    queryKey: ['admin', 'analytics', 'summary'],
    queryFn: () => get<SummaryResponse>('/admin/analytics/summary'),
    staleTime: 30_000,
  });

  const signupQuery = range.kind === 'preset'
    ? `days=${range.days}`
    : `from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
  const { data: signups, isLoading: loadingSignups } = useQuery({
    queryKey: ['admin', 'analytics', 'signups', signupQuery],
    queryFn: () => get<SignupsResponse>(`/admin/analytics/signups?${signupQuery}`),
    staleTime: 30_000,
  });

  return (
    <>
      <h1 className={styles.h1}>Analytics</h1>
      <p className={styles.sub}>
        Standing totals + signup growth + plan breakdown. Active counts come
        from the audit log (distinct user actor over the last 7 / 30 days).
      </p>

      {loadingSummary || !summary ? (
        <p className={styles.muted}>Loading…</p>
      ) : (
        <>
          <div className={styles.cardsRow}>
            <Card label="Total users" value={summary.totals.users} />
            <Card label="Total accounts" value={summary.totals.companies} />
            <Card label="Team accounts" value={summary.teamAccountCount} hint={`${summary.totals.companies > 0 ? Math.round((summary.teamAccountCount / summary.totals.companies) * 100) : 0}% of accounts have 2+ members`} />
            <Card label="Active (30d)" value={summary.mauLast30d} hint={`${summary.wauLast7d} active in last 7 days`} />
            <Card
              label="Signups this month"
              value={summary.signupsThisMonth}
              hint={summary.signupsLastMonth > 0 ? `${summary.signupsLastMonth} last month` : 'no prior month data'}
              trend={trendOf(summary.signupsThisMonth, summary.signupsLastMonth)}
            />
            <Card
              label="MRR (est.)"
              value={`${dollars(summary.mrrCents)}/mo`}
              hint={(
                <>
                  Plans {dollars(summary.mrrBreakdown.plansCents)}, add-ons {dollars(summary.mrrBreakdown.addonsCents)}, widget tiers {dollars(summary.mrrBreakdown.widgetTiersCents)}
                  <br />
                  Enterprise plans are hand-billed and left out
                </>
              )}
            />
            <Card
              label="Add-ons"
              value={summary.pricing.workspacesWithAddons}
              hint={`workspace${summary.pricing.workspacesWithAddons === 1 ? '' : 's'} renewing add-ons, ${dollars(summary.mrrBreakdown.addonsCents)}/mo`}
            />
            <Card
              label="Widget tiers"
              value={summary.pricing.widgetTiers.starter + summary.pricing.widgetTiers.growth + summary.pricing.widgetTiers.scale}
              hint={`Starter ${summary.pricing.widgetTiers.starter}, Growth ${summary.pricing.widgetTiers.growth}, Scale ${summary.pricing.widgetTiers.scale}`}
            />
            <NeedsAPersonCard attention={summary.pricing.attention} />
            <LimitsReachedCard reached={summary.pricing.limitsReachedThisMonth} />
            <Card label="Total tickets" value={summary.totals.tickets} hint={`${summary.totals.projects} projects`} />
            <Card
              label="Widget submissions"
              value={summary.widget.submissions}
              hint={summary.widget.active > 0
                ? `${summary.widget.active} live widget${summary.widget.active === 1 ? '' : 's'} of ${summary.widget.configured} configured`
                : summary.widget.configured > 0
                  ? `${summary.widget.configured} configured, none live yet`
                  : 'no widgets configured yet'}
            />
          </div>

          <div className={styles.section}>
            <h2 style={{ fontSize: 16, marginTop: 0, marginBottom: 14 }}>Plan breakdown</h2>
            <PlanBar breakdown={summary.planBreakdown} total={summary.totals.companies} />
          </div>
        </>
      )}

      <DailyBriefPanel />

      <div className={styles.section}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ fontSize: 16, margin: 0 }}>Signups per day</h2>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {([30, 90, 180] as const).map((d) => {
              const active = range.kind === 'preset' && range.days === d;
              return (
                <button
                  key={d}
                  type="button"
                  className={styles.expandBtn}
                  onClick={() => setRange({ kind: 'preset', days: d })}
                  style={active ? { background: 'var(--bg-muted)', color: 'var(--text)' } : undefined}
                >
                  {d}d
                </button>
              );
            })}
            <span className={styles.muted} style={{ fontSize: 11, margin: '0 6px' }}>or</span>
            <CustomRangePicker
              value={range.kind === 'custom' ? range : null}
              onChange={(r) => setRange({ kind: 'custom', from: r.from, to: r.to })}
            />
          </div>
        </div>
        {loadingSignups || !signups ? (
          <p className={styles.muted}>Loading…</p>
        ) : signups.series.every((d) => d.signups === 0) ? (
          <p className={styles.muted}>No signups in the selected window.</p>
        ) : (
          <SignupBarChart series={signups.series} />
        )}
      </div>
    </>
  );
}

function Card({ label, value, hint, trend, title }: {
  label: string;
  value: number | string;
  hint?: React.ReactNode;
  trend?: 'up' | 'down' | 'flat';
  title?: string;
}) {
  const TrendIcon = trend === 'up' ? ArrowUp : trend === 'down' ? ArrowDown : trend === 'flat' ? Minus : null;
  const trendColor = trend === 'up' ? 'var(--success, #16a34a)' : trend === 'down' ? 'var(--danger, #dc2626)' : 'var(--text-tertiary)';
  return (
    <div className={styles.card} title={title}>
      <div className={styles.cardLabel}>{label}</div>
      <div className={styles.cardValue}>
        {value}
        {TrendIcon ? <TrendIcon size={14} style={{ color: trendColor, marginLeft: 6 }} /> : null}
      </div>
      {hint ? <div className={styles.cardHint}>{hint}</div> : null}
    </div>
  );
}

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(0)}`;
}

const ATTENTION_LABELS: Array<[key: keyof SummaryResponse['pricing']['attention'], label: string]> = [
  ['addonChangesWaiting', 'add-on changes not on Stripe after a day'],
  ['obligationsOpen', 'refunds or tier changes owed, not done'],
  ['addonSubscriptionsPastDue', 'add-on subscriptions past due'],
  ['tiersNotInUse', 'widget tiers held but not in use'],
  ['tiersPlanCannotUse', 'widget tiers renewing that the plan cannot use'],
];

/** Pricing states a person should look at (TOADO-655): each count above zero, or Nothing. */
function NeedsAPersonCard({ attention }: { attention: SummaryResponse['pricing']['attention'] }) {
  const open = ATTENTION_LABELS.filter(([key]) => attention[key] > 0);
  return (
    <Card
      label="Needs a person"
      value={open.length === 0 ? 'Nothing' : open.reduce((sum, [key]) => sum + attention[key], 0)}
      hint={open.length === 0 ? undefined : (
        <>
          {open.map(([key, label], i) => (
            <span key={key}>{i > 0 && <br />}{attention[key]} {label}</span>
          ))}
        </>
      )}
    />
  );
}

const METER_LABELS: Array<[key: string, label: string]> = [
  ['recording', 'Recording'],
  ['narration', 'Narration'],
  ['widget_reports', 'Widget reports'],
  ['visitor_minutes', 'Visitor minutes'],
];

/** Workspaces at 100% of a meter this month, by meter. One at two limits counts under each. */
function LimitsReachedCard({ reached }: { reached: Record<string, number> }) {
  const known = new Set(METER_LABELS.map(([key]) => key));
  const hits = [
    ...METER_LABELS.map(([key, label]): [string, number] => [label, reached[key] ?? 0]),
    // A meter this build has no label for still counts, by its key.
    ...Object.entries(reached).filter(([key]) => !known.has(key)),
  ].filter(([, n]) => n > 0);
  return (
    <Card
      label="At a limit this month"
      value={hits.length === 0 ? 'None' : hits.reduce((sum, [, n]) => sum + n, 0)}
      title="Workspaces that reached 100% of a meter this month. One at two limits counts under each."
      hint={hits.length === 0 ? undefined : hits.map(([label, n]) => `${label} ${n}`).join(', ')}
    />
  );
}

function trendOf(current: number, prior: number): 'up' | 'down' | 'flat' {
  if (prior === 0 && current === 0) return 'flat';
  if (prior === 0) return 'up';
  if (current > prior) return 'up';
  if (current < prior) return 'down';
  return 'flat';
}

function PlanBar({ breakdown, total }: { breakdown: Record<string, number>; total: number }) {
  if (total === 0) return <p className={styles.muted}>No accounts.</p>;
  const order: Array<keyof typeof PLAN_COLORS> = ['free', 'personal', 'team', 'business', 'enterprise'];
  return (
    <>
      <div style={{ display: 'flex', width: '100%', height: 14, borderRadius: 7, overflow: 'hidden', background: 'var(--bg-muted)', marginBottom: 12 }}>
        {order.map((plan) => {
          const n = breakdown[plan] ?? 0;
          if (n === 0) return null;
          const pct = (n / total) * 100;
          return (
            <div
              key={plan}
              style={{ width: `${pct}%`, background: PLAN_COLORS[plan], minWidth: pct > 0 ? 2 : 0 }}
              title={`${plan}: ${n} (${pct.toFixed(1)}%)`}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12 }}>
        {order.map((plan) => (
          <div key={plan} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ width: 10, height: 10, borderRadius: 2, background: PLAN_COLORS[plan] }} />
            <span style={{ textTransform: 'capitalize' }}>{plan}</span>
            <span className={styles.muted}>{breakdown[plan] ?? 0}</span>
          </div>
        ))}
      </div>
    </>
  );
}

// Business is cyan rather than its badge's blue: beside Personal in the
// stacked bar, two blues read as one segment.
const PLAN_COLORS = {
  free: '#9ca3af',
  personal: '#2563eb',
  team: '#059669',
  business: '#0891b2',
  enterprise: '#7e22ce',
};

function SignupBarChart({ series }: { series: Array<{ date: string; signups: number }> }) {
  const max = Math.max(1, ...series.map((d) => d.signups));
  const total = series.reduce((acc, d) => acc + d.signups, 0);
  // Layout: flex row of bars, bar height proportional to value over max,
  // x-axis tick every ~7 days for readability.
  const tickEvery = Math.max(1, Math.floor(series.length / 8));
  return (
    <>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 8 }}>
        {total} signups · peak {max} on one day
      </div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height: 120 }}>
        {series.map((d) => {
          const h = d.signups === 0 ? 1 : Math.max(2, Math.round((d.signups / max) * 120));
          return (
            <div
              key={d.date}
              style={{
                flex: 1,
                minWidth: 2,
                height: h,
                background: d.signups === 0 ? 'var(--border)' : 'var(--accent, #f97316)',
                borderRadius: 2,
                opacity: d.signups === 0 ? 0.5 : 1,
              }}
              title={`${d.date}: ${d.signups} signup${d.signups === 1 ? '' : 's'}`}
            />
          );
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text-tertiary)', marginTop: 6 }}>
        {series.filter((_, i) => i % tickEvery === 0).map((d) => (
          <span key={d.date}>{formatShortDay(d.date)}</span>
        ))}
      </div>
    </>
  );
}

/**
 * Daily admin brief — preview block + "Send now" button. Hits
 * GET /admin/brief/preview to fetch yesterday's metrics block
 * (same shape that goes in the email body) and POST /admin/brief/
 * send-now to trigger an out-of-band send via the BullMQ queue.
 *
 * The full content of the email is already represented across the
 * cards above + the signup chart; this panel just makes the brief
 * machinery visible + testable from the panel.
 */
interface BriefMetrics {
  date: string;
  totalUsers: number;
  newSignupsYesterday: number;
  ticketsYesterday: number;
  churnedYesterday: number;
  mrrCents: number;
  estimatedCostUsdYesterday: number;
}

function DailyBriefPanel() {
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'brief', 'preview'],
    queryFn: () => get<{ metrics: BriefMetrics }>('/admin/brief/preview'),
    staleTime: 60_000,
  });
  const sendNow = useMutation({
    mutationFn: () => post('/admin/brief/send-now'),
  });

  return (
    <div className={styles.section}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <h2 style={{ fontSize: 16, margin: 0 }}>Daily admin brief</h2>
          <p style={{ fontSize: 12, color: 'var(--text-tertiary)', margin: '4px 0 0' }}>
            Fires at 13:00 UTC daily to every staff user. Click Send now to fire
            an out-of-band send (useful for testing the email template).
          </p>
        </div>
        <button
          type="button"
          className={styles.expandBtn}
          disabled={sendNow.isPending}
          onClick={() => sendNow.mutate()}
        >
          <Mail size={12} style={{ marginRight: 6 }} />
          {sendNow.isPending ? 'Queuing…' : sendNow.isSuccess ? 'Queued ✓' : 'Send now'}
        </button>
      </div>
      {isLoading || !data ? (
        <p className={styles.muted}>Loading preview…</p>
      ) : (
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          Yesterday ({data.metrics.date}): {data.metrics.newSignupsYesterday} signups · {data.metrics.ticketsYesterday} tickets · {data.metrics.churnedYesterday} cancellations · est. cost ${data.metrics.estimatedCostUsdYesterday.toFixed(2)}
        </div>
      )}
    </div>
  );
}

/**
 * Two date inputs + Apply. Held in local state so half-typed dates
 * don't trigger a refetch on every keystroke — the parent only sees
 * a committed pair via onChange.
 */
function CustomRangePicker(props: {
  value: { from: string; to: string } | null;
  onChange: (r: { from: string; to: string }) => void;
}) {
  const today = new Date().toISOString().slice(0, 10);
  const defaultFrom = props.value?.from ?? new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const defaultTo = props.value?.to ?? today;
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);
  const dirty = from !== (props.value?.from ?? '') || to !== (props.value?.to ?? '');
  const valid = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <input
        type="date"
        value={from}
        max={today}
        onChange={(e) => setFrom(e.target.value)}
        style={dateInputStyle}
      />
      <span className={styles.muted} style={{ fontSize: 11 }}>→</span>
      <input
        type="date"
        value={to}
        max={today}
        onChange={(e) => setTo(e.target.value)}
        style={dateInputStyle}
      />
      <button
        type="button"
        className={styles.expandBtn}
        disabled={!valid || !dirty}
        onClick={() => props.onChange({ from, to })}
      >
        Apply
      </button>
    </div>
  );
}

const dateInputStyle: React.CSSProperties = {
  padding: '4px 6px',
  fontSize: 12,
  fontFamily: 'inherit',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'inherit',
};

function formatShortDay(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }); // a day, not a moment (TOADO-904)
  } catch {
    return iso;
  }
}
